import { useState, useEffect } from 'react'
import '../styles/components/booking-form.css'

function BookingForm({ preferences, onComplete, onBack }) {
  const [bookingOption, setBookingOption] = useState(null)
  const [quickTime, setQuickTime] = useState(null)
  const [customDate, setCustomDate] = useState('')
  const [customTime, setCustomTime] = useState('20:00')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [deadlineOption, setDeadlineOption] = useState(null)
  const [customDeadline, setCustomDeadline] = useState('')

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    const dateInput = document.getElementById('bookingDate')
    if (dateInput) {
      dateInput.min = today
    }
    
    const deadlineInput = document.getElementById('deadlineDate')
    if (deadlineInput) {
      deadlineInput.min = today
    }
  }, [])

  const setQuickTimeOption = (option) => {
    setQuickTime(option)
    setShowCustomInput(option === 'custom')
    
    if (option !== 'custom') {
      const now = new Date()
      let targetDate = new Date()
      
      switch (option) {
        case 'tonight':
          targetDate.setHours(20, 0, 0, 0)
          break
        case 'tomorrow':
          targetDate.setDate(now.getDate() + 1)
          targetDate.setHours(20, 0, 0, 0)
          break
        case 'weekend':
          const daysUntilSaturday = (6 - now.getDay()) % 7 || 7
          targetDate.setDate(now.getDate() + daysUntilSaturday)
          targetDate.setHours(18, 0, 0, 0)
          break
        default:
          return
      }
      
      setCustomDate(targetDate.toISOString().split('T')[0])
      setCustomTime(targetDate.toTimeString().slice(0, 5))
    }
  }

  const setDeadlineOptionHandler = (option) => {
    setDeadlineOption(option)
    
    if (option === 'custom') {
      // Set default to 7 days from now
      const defaultDate = new Date()
      defaultDate.setDate(defaultDate.getDate() + 7)
      setCustomDeadline(defaultDate.toISOString().split('T')[0])
    } else if (option === 'none') {
      setCustomDeadline('')
    } else {
      // Quick options: 1 day, 3 days, 1 week
      const now = new Date()
      const targetDate = new Date()
      
      switch (option) {
        case '1day':
          targetDate.setDate(now.getDate() + 1)
          break
        case '3days':
          targetDate.setDate(now.getDate() + 3)
          break
        case '1week':
          targetDate.setDate(now.getDate() + 7)
          break
      }
      
      setCustomDeadline(targetDate.toISOString().split('T')[0])
    }
  }

  const getBookingPreview = () => {
    if (!quickTime) return 'Vælg tidspunkt ovenfor'
    
    if (quickTime === 'custom' && !customDate) {
      return 'Vælg dato og tid'
    }
    
    const date = new Date(`${customDate}T${customTime}`)
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
    
    return date.toLocaleDateString('da-DK', options)
  }

  const handleProceed = () => {
    const deadline = deadlineOption === 'none' || !deadlineOption ? null : customDeadline
    
    if (bookingOption === 'skip') {
      const result = deadline ? {
        booking: null,
        deadline: deadline
      } : null
      onComplete(result)
    } else if (bookingOption === 'add' && quickTime && customDate) {
      const booking = {
        date: customDate,
        time: customTime,
        quickTime: quickTime,
        deadline: deadline
      }
      onComplete(booking)
    }
  }

  const canProceed = () => {
    if (bookingOption === 'skip') return true
    if (bookingOption === 'add') {
      return quickTime && customDate
    }
    return false
  }

  return (
    <div className="booking-form step active">
      <h2>📅 Foreslå tidspunkt (valgfrit)</h2>
      
      <div className="optional-booking-section">
        <p style={{ color: 'var(--text-secondary)', marginBottom: '25px', textAlign: 'center' }}>
          Du kan valgfrit foreslå et tidspunkt hvor I kan gennemgå kortet sammen, eller springe dette over.
        </p>
        
        <div className="booking-options">
          <div 
            className={`booking-choice-btn ${bookingOption === 'skip' ? 'selected' : ''}`}
            onClick={() => setBookingOption('skip')}
          >
            ⏭️ Spring over<br />
            <small>Send kortet uden tidspunkt</small>
          </div>
          <div 
            className={`booking-choice-btn ${bookingOption === 'add' ? 'selected' : ''}`}
            onClick={() => setBookingOption('add')}
          >
            📅 Foreslå tidspunkt<br />
            <small>Gør det konkret</small>
          </div>
        </div>
        
        {bookingOption === 'add' && (
          <div className="booking-details show">
            <h3 style={{ marginBottom: '20px' }}>📅 Hvornår skal det være?</h3>
            
            <div className="quick-time-options">
              <div 
                className={`quick-time-btn ${quickTime === 'tonight' ? 'active' : ''}`}
                onClick={() => setQuickTimeOption('tonight')}
              >
                🌙 I aften
              </div>
              <div 
                className={`quick-time-btn ${quickTime === 'tomorrow' ? 'active' : ''}`}
                onClick={() => setQuickTimeOption('tomorrow')}
              >
                ☀️ I morgen
              </div>
              <div 
                className={`quick-time-btn ${quickTime === 'weekend' ? 'active' : ''}`}
                onClick={() => setQuickTimeOption('weekend')}
              >
                🍷 På weekend
              </div>
              <div 
                className={`quick-time-btn ${quickTime === 'custom' ? 'active' : ''}`}
                onClick={() => setQuickTimeOption('custom')}
              >
                📅 Vælg selv
              </div>
            </div>
            
            {showCustomInput && (
              <div className="datetime-input">
                <div className="input-group">
                  <label htmlFor="bookingDate">📅 Dato</label>
                  <input 
                    type="date" 
                    id="bookingDate"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="bookingTime">🕐 Tidspunkt</label>
                  <input 
                    type="time" 
                    id="bookingTime"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                  />
                </div>
              </div>
            )}
            
            {quickTime && customDate && (
              <div className="booking-preview">
                <h3>📅 Dit forslag:</h3>
                <p>{getBookingPreview()}</p>
              </div>
            )}
          </div>
        )}

        {/* Deadline Section */}
        <div className="deadline-section" style={{
          marginTop: '32px',
          paddingTop: '32px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ marginBottom: '16px', fontSize: '1.1em' }}>
            ⏰ Deadline for svar (valgfrit)
          </h3>
          <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Vælg om modtageren skal have en deadline for at svare på kortet
          </p>
          
          <div className="deadline-options" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div 
              className={`quick-time-btn ${deadlineOption === 'none' ? 'active' : ''}`}
              onClick={() => setDeadlineOptionHandler('none')}
            >
              Ingen deadline
            </div>
            <div 
              className={`quick-time-btn ${deadlineOption === '1day' ? 'active' : ''}`}
              onClick={() => setDeadlineOptionHandler('1day')}
            >
              I morgen
            </div>
            <div 
              className={`quick-time-btn ${deadlineOption === '3days' ? 'active' : ''}`}
              onClick={() => setDeadlineOptionHandler('3days')}
            >
              Om 3 dage
            </div>
            <div 
              className={`quick-time-btn ${deadlineOption === '1week' ? 'active' : ''}`}
              onClick={() => setDeadlineOptionHandler('1week')}
            >
              Om 1 uge
            </div>
            <div 
              className={`quick-time-btn ${deadlineOption === 'custom' ? 'active' : ''}`}
              onClick={() => setDeadlineOptionHandler('custom')}
            >
              Vælg dato
            </div>
          </div>

          {deadlineOption === 'custom' && (
            <div className="input-group" style={{ marginTop: '16px' }}>
              <label htmlFor="deadlineDate">📅 Deadline dato</label>
              <input 
                type="date" 
                id="deadlineDate"
                value={customDeadline}
                onChange={(e) => setCustomDeadline(e.target.value)}
              />
            </div>
          )}

          {deadlineOption && deadlineOption !== 'none' && customDeadline && (
            <div className="deadline-preview" style={{
              marginTop: '16px',
              padding: '12px',
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '8px',
              fontSize: '0.9em'
            }}>
              <strong>Deadline:</strong> {new Date(customDeadline).toLocaleDateString('da-DK', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          )}
        </div>
      </div>
      
      <div className="button-group">
        <button className="btn btn-secondary" onClick={onBack}>
          ← Tilbage
        </button>
        <button 
          className="btn" 
          onClick={handleProceed}
          disabled={!canProceed()}
        >
          Næste
        </button>
      </div>
    </div>
  )
}

export default BookingForm
